import { Button, Modal, Popconfirm } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import React, { FC, useState } from 'react';
import { AOPDecisionStep, AOPEscalation, AOPParty } from '../../../types/AOPTypes';
import { AOPSelect, AOPSelectOption, AOPTextArea } from '../fields/AOPFields';
import { AOPPanelField } from './AOPPanelShell';
import { AOPDurationField } from './AOPDurationField';
import {
	AOP_CHANNELS,
	AOP_MAX_ESCALATIONS,
	AOP_PARTIES,
	createEscalation,
	withEscalations,
} from '../utils/AOPPlanUtils';

/**
 * The reminders a decision sends while it waits: who to nudge, how, with what
 * message, and how long after the last one.
 *
 * Its own file, apart from the question the step is asking, because the two
 * share only the step they are editing — and because whether a decision has
 * reminders at all decides which branch it gives up through, which is the one
 * thing here that reaches outside this tab.
 */

const PARTY_OPTIONS: AOPSelectOption[] = AOP_PARTIES.map((party) => ({
	value: party.value,
	label: party.label,
}));

const CHANNEL_OPTIONS: AOPSelectOption[] = AOP_CHANNELS.map((channel) => ({
	value: channel.value,
	label: channel.label,
}));

interface AOPDecisionEscalationsProps {
	step: AOPDecisionStep;
	isReadOnly: boolean;
	onChange: (patch: Partial<AOPDecisionStep>) => void;
	/** The last reminder has gone, so this tab is closing behind the author. */
	onEmptied: () => void;
}

export const AOPDecisionEscalations: FC<AOPDecisionEscalationsProps> = ({
	step,
	isReadOnly,
	onChange,
	onEmptied,
}) => {
	const escalations = step.escalations || [];

	/*
	  How many reminders have been removed. Only the count matters: it is what
	  the escalation cards key on, so a removal gives them all fresh fields and
	  no card can be handed the text of the one that used to be in its place.
	*/
	const [removals, setRemovals] = useState(0);

	/**
	 * Timeout or Escalation is the catch-all exit's name, which the service ties
	 * to whether the step has escalations: `on_exhausted` once it does,
	 * `on_timeout` while it does not. So the two move together — picking
	 * Escalation with nothing to escalate, or Timeout with escalations left in
	 * place, is rejected on save.
	 */
	/**
	 * Taking the last reminder away puts the step back on Timeout.
	 *
	 * Having reminders is what Escalation mode *is* — the service refuses a
	 * decision that gives up through the escalation branch with nothing to
	 * exhaust — so removing the last one is a mode change, and it is said out
	 * loud because the author asked to delete a card, not to change how the step
	 * gives up.
	 */
	const confirmBackToTimeout = (onConfirm: () => void): void => {
		Modal.confirm({
			title: 'Removing the last reminder puts this step back on Timeout',
			content: (
				<span className="bodyregular text-character-primary">
					A step with no reminders waits once and then follows its Timeout path. The branch it gives
					up through keeps pointing at the same step, and this tab closes until you choose
					Escalation again.
				</span>
			),
			okText: 'Continue',
			cancelText: 'Cancel',
			onOk: onConfirm,
		});
	};

	const patchEscalation = (index: number, patch: Partial<AOPEscalation>) =>
		onChange({
			escalations: escalations.map((escalation, position) =>
				position === index ? { ...escalation, ...patch } : escalation
			),
		});

	return (
		<>
			{/*
			  `timeout_seconds` is a quiet watch before anybody is contacted — the
			  wait the first escalation follows. It lives here rather than on the
			  Question tab because it is the first beat of this sequence.
			*/}
			<AOPPanelField
				label="Initial wait"
				required
				help="How long Radar watches before contacting anyone."
			>
				<AOPDurationField
					ariaLabel="Initial wait"
					seconds={step.timeout_seconds}
					disabled={isReadOnly}
					onCommit={(timeout_seconds) => onChange({ timeout_seconds })}
				/>
			</AOPPanelField>

			{escalations.length === 0 ? (
				<span className="bodyregular text-character-secondary">
					No escalations. Radar waits out the decision's timeout and then takes the Timeout branch.
				</span>
			) : null}

			{escalations.map((escalation, index) => (
				/*
				  Keyed on removals, not on the length of the list.

				  The refusal and the half-typed text live in the field, which keys
				  its own state by the text it was given — so two reminders holding
				  the same message are indistinguishable to it, and deleting one let
				  the next inherit the first's blank draft and its complaint.

				  Only a removal shifts the positions: reminders are added at the
				  end. Keying on the length disturbed every card on an add too, and
				  took with it any message an author had been refused and not yet
				  corrected — losing work on an action that should not have touched
				  it. This changes when, and only when, the indices actually move.
				*/
				<div
					key={`${removals}:${index}`}
					className="flex flex-col gap-m rounded border border-solid border-grey-4 p-s"
				>
					<div className="flex items-center">
						<span className="footnoteMedium inline-flex h-6 w-6 items-center justify-center rounded-full bg-purple-1 text-purple-6">
							{index + 1}
						</span>
						<span className="flex-1" />
						{isReadOnly ? null : (
							<Popconfirm
								title="Remove this escalation?"
								okText="Remove"
								okButtonProps={{ danger: true }}
								cancelText="Cancel"
								onConfirm={() => {
									const left = escalations.filter((other) => other !== escalation);
									// Counted where the removal is applied, not here: taking the
									// last reminder away asks a second question first, and
									// answering no to that leaves the list as it was. Bumping
									// ahead of it remounted the cards anyway and took a
									// half-typed message with them.
									const remove = (patch: Partial<AOPDecisionStep>) => {
										setRemovals((count) => count + 1);
										onChange(patch);
									};

									if (left.length > 0) {
										remove(withEscalations(step, left));
										return;
									}

									// The last one: the step goes back to Timeout and this tab
									// closes, so the author is returned to the question.
									confirmBackToTimeout(() => {
										remove(withEscalations(step, left));
										onEmptied();
									});
								}}
							>
								<Button
									type="text"
									aria-label={`Remove escalation ${index + 1}`}
									icon={<DeleteOutlined />}
								/>
							</Popconfirm>
						)}
					</div>

					<AOPPanelField label="Action" required>
						<AOPSelect
							ariaLabel="Action"
							value={escalation.channel}
							options={CHANNEL_OPTIONS}
							disabled={isReadOnly}
							onChange={(channel) => patchEscalation(index, { channel } as Partial<AOPEscalation>)}
						/>
					</AOPPanelField>

					<AOPPanelField label="Recipient" required>
						<AOPSelect
							ariaLabel="Recipient"
							value={escalation.party}
							options={PARTY_OPTIONS}
							disabled={isReadOnly}
							onChange={(party) => patchEscalation(index, { party: party as AOPParty })}
						/>
					</AOPPanelField>

					<AOPPanelField label="Message body" required>
						<AOPTextArea
							ariaLabel="Message body"
							rows={3}
							value={escalation.message}
							disabled={isReadOnly}
							// Required upstream. Sending an empty one takes the whole plan
							// patch down and comes back as a save that failed, saying
							// nothing about which reminder or which rule.
							// Required upstream, and the refusal belongs to the field: held
							// here it needed an identity, and neither the position nor the
							// object survived an ordinary edit.
							validate={(message) =>
								message.trim().length === 0 ? 'A reminder needs something to say.' : null
							}
							onCommit={(message) => patchEscalation(index, { message })}
						/>
					</AOPPanelField>

					<AOPPanelField label="Response window" required>
						<AOPDurationField
							ariaLabel="Response window"
							seconds={escalation.window_seconds}
							disabled={isReadOnly}
							onCommit={(window_seconds) => patchEscalation(index, { window_seconds })}
						/>
					</AOPPanelField>
				</div>
			))}

			{isReadOnly || escalations.length >= AOP_MAX_ESCALATIONS ? null : (
				<Button
					type="link"
					className="self-start px-0"
					icon={<PlusOutlined />}
					onClick={() => {
						const added = escalations.concat([createEscalation()]);

						// No mode change to explain: this tab is only open once the step
						// already has one, so adding another only joins the sequence.
						onChange(withEscalations(step, added));
					}}
				>
					Add escalation step
				</Button>
			)}
		</>
	);
};
